import React, { Component } from 'react';
import T from 'prop-types';
// components
import FilterItem from 'components/FarmFeed/SearchFilters/FilterItem';
import SearchList from 'components/SearchList/SearchList';
import { CompanyLine, FarmLine, PigGroupLine } from 'components/SearchList/Lines/Lines';
import Preloader from 'components/Preloader';
import { FormattedMessage } from 'react-intl';
import Waypoint from 'react-waypoint';
// utils
import withOutsideClick from 'react-onclickoutside';
import { fetchFromAPI } from 'utils/api';
import { toastResponseErrors } from 'utils/responseErrorsHelper';

const endpoints = {
  company: '/companies/search',
  farm: '/farms/search',
  group: '/pig_groups/search',
};

const PER_PAGE = 25;

const initialFetchState = {
  resources: [],
  isLoading: false,
  page: 1,
  total: 0,
  search: '',
};

class FilterItemContainer extends Component {

  state = initialFetchState;

  fetchList = ({ search, page = 1 } = {}) => {
    const { filterType, reqParams } = this.props;
    const nextSearch = search !== undefined ? search : this.state.search;
    this.setState({ isLoading: true });
    return fetchFromAPI(endpoints[filterType], {
      params: { ...reqParams, search: nextSearch, page, per_page: PER_PAGE },
    })
      .then(({ resources, meta }) => {
        this.setState((prev) => ({
          isLoading: false,
          resources: page === 1 ? resources : prev.resources.concat(resources),
          page,
          search: nextSearch,
          total: (meta && meta.total) || resources.length,
        }));
      })
      .catch((error) => {
        this.setState({ isLoading: false });
        return toastResponseErrors(error);
      });
  };

  onOpen = () => {
    const { resources } = this.state;
    const { onOpen, isOpened, filterType } = this.props;
    if (isOpened) return;
    onOpen(filterType);
    if (!resources.length) this.fetchList();
  };

  handleClickOutside = () => {
    const { onOpen, isOpened } = this.props;
    if (isOpened) {
      onOpen('');
      this.setState(initialFetchState);
    }
  };

  onSelect = (e, item) => {
    const { onChange, onOpen } = this.props;
    e.stopPropagation();
    onChange(item);
    onOpen('');
  };

  onClear = (e) => {
    const { onChange, isOpened, onOpen } = this.props;
    e.stopPropagation();
    onChange({});
    if (isOpened) onOpen('');
  };

  handleSearchChange = (search) => {
    this.fetchList({ search, page: 1 });
  };

  loadMore = () => {
    const { page, isLoading } = this.state;
    if (isLoading) return;
    this.fetchList({ page: page + 1 });
  };

  renderItemLine = (lineData) => {
    const { filterType, value } = this.props;
    const defaultProps = {
      key: lineData.id,
      name: lineData.name,
      onClick: (e) => this.onSelect(e, lineData),
      isDisabled: lineData.id === value.id,
    };
    switch (filterType) {
      case 'company': {
        return <CompanyLine {...defaultProps} />;
      }
      case 'farm': {
        return <FarmLine {...defaultProps} />;
      }
      case 'group': {
        return <PigGroupLine {...defaultProps} pigGroup={lineData} />;
      }
      default:
        return null;
    }
  };

  render() {
    const { resources, isLoading, total } = this.state;
    const { value, filterType, isOpened, isDisabled } = this.props;
    const hasMore = !isLoading && resources.length < total;
    return (
      <FilterItem
        label={<FormattedMessage id={`general.${filterType}`} />}
        placeholder={<FormattedMessage id={`general.select_${filterType}`} />}
        isOpened={isOpened}
        isDisabled={isDisabled}
        value={value.name}
        onOpen={this.onOpen}
        onClear={this.onClear}
      >
        <SearchList onSearchChange={this.handleSearchChange}>
          <Preloader isActive={isLoading} />
          {resources.map(this.renderItemLine)}
          {hasMore && <Waypoint onEnter={this.loadMore} />}
        </SearchList>
      </FilterItem>
    );
  }

}

FilterItemContainer.propTypes = {
  value: T.oneOfType([T.string, T.object]),
  onOpen: T.func.isRequired,
  onChange: T.func.isRequired,
  isOpened: T.bool.isRequired,
  isDisabled: T.bool.isRequired,
  filterType: T.string.isRequired,
  reqParams: T.object.isRequired,
};

export default withOutsideClick(FilterItemContainer);
